import { getStorageSession, registerStorageResponse } from './storageUrls.js';
/**
 * VisionAssets adapter — uploading a scout image to a project and listing a project's
 * images on top of the `protos.vision.assets.v1.VisionAssets` gRPC-web service plus the
 * binary `apps/media` HTTP edge.
 *
 * The flow is two hops:
 *   1. The image bytes are streamed to `apps/media` (HTTP), which converts/thumbnails them,
 *      stores them in the private Vision bucket, and returns the minted asset id.
 *   2. `FinalizeProjectImage` (gRPC) commits the `vision_assets` row + charges owner storage.
 * `ListProjectImages` returns each image with short-TTL presigned view URLs.
 */

import { createClient, Code, ConnectError } from '@connectrpc/connect';
import { createGrpcWebTransport } from '@connectrpc/connect-web';
import { ErrorInfoSchema } from '@buf/googleapis_googleapis.bufbuild_es/google/rpc/error_details_pb';
import { VisionAssets } from '../gen/vision/assets/v1/assets_service_pb';
import {
  projectImageToLegacy,
  listResponseToImages,
  referenceImageToLegacy,
  colorPresetToLegacy,
} from './visionAssetsMapping';
import { regionsToProto } from './visionMaskRegionsMapping.js';
import { config } from '../config';
import { getIdTokenForRequest } from './firebaseClient.js';

const PRESET_LIMIT_REACHED = 'PRESET_LIMIT_REACHED';
const STORAGE_LIMIT_EXCEEDED = 'STORAGE_LIMIT_EXCEEDED';

const BASE_URL = config.VISION_GRPC_BASE_URL;
const MEDIA_BASE_URL = config.VISION_MEDIA_BASE_URL;

const PAGE_SIZE = 100;

const MEDIA_UPLOAD_TIMEOUT_MS = 120_000;

const authInterceptor = (next) => async (req) => {
  const token = await getIdTokenForRequest();
  if (token) {
    req.header.set('authorization', `Bearer ${token}`);
  }
  const session = getStorageSession();
  const response = await next(req);
  registerStorageResponse(response.message, session);
  return response;
};

let cachedClient = null;

function client() {
  if (!cachedClient) {
    const transport = createGrpcWebTransport({
      baseUrl: BASE_URL,
      interceptors: [authInterceptor],
    });
    cachedClient = createClient(VisionAssets, transport);
  }
  return cachedClient;
}

/**
 * Stream one image file to the apps/media edge. Resolves to
 * `{ assetId, clientUploadId }` for the finalize hop.
 */
export async function uploadImageBytes(projectId, file, { locationId, clientUploadId, onProgress, signal } = {}) {
  const token = await getIdTokenForRequest();
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error('Upload cancelled'));
      return;
    }
    const formData = new FormData();
    formData.append('image', file);
    formData.append('client_upload_id', clientUploadId);
    if (locationId) {
      formData.append('location_id', locationId);
    }

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${MEDIA_BASE_URL}/vision/projects/${projectId}/images`);

    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress({
          loaded: event.loaded,
          total: event.total,
          percent: Math.round((event.loaded / event.total) * 100),
        });
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch {
          reject(new Error('Failed to parse the media upload response.'));
        }
      } else if (xhr.status === 413) {
        const err = new Error('Storage limit exceeded');
        err.code = 'STORAGE_LIMIT_EXCEEDED';
        reject(err);
      } else if (xhr.status === 403 || xhr.status === 404) {
        reject(new Error('You do not have access to upload to this project.'));
      } else {
        reject(new Error('Upload failed. Please try again.'));
      }
    };

    xhr.onerror = () => reject(new Error('Network error. Please check your connection.'));
    xhr.ontimeout = () => reject(new Error('Upload timed out. Please try again.'));

    if (signal) {
      signal.addEventListener('abort', () => {
        xhr.abort();
        reject(new Error('Upload cancelled'));
      });
    }

    xhr.send(formData);
  });
}

/** Commit an uploaded image's metadata; resolves to the legacy image record. */
export async function finalizeProjectImage({ projectId, assetId, locationId, clientUploadId }) {
  const response = await client().finalizeProjectImage({
    clientUploadId,
    params: { projectId, assetId, locationId: locationId || '' },
  });

  if (!response.result?.image) {
    throw new Error('FinalizeProjectImage returned no image');
  }

  return projectImageToLegacy(response.result.image);
}

// crypto.randomUUID is only defined in secure contexts (HTTPS or localhost); fall back
// to a non-crypto unique id for HTTP origins. Generated once per file and reused across
// retries so the server can dedupe a re-sent upload (idempotent finalize).
export function newClientUploadId() {
  return globalThis.crypto?.randomUUID?.() ?? `u-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Upload one file end-to-end (bytes -> finalize); resolves to the legacy image record. */
export async function uploadProjectImage(projectId, file, { locationId, clientUploadId, onProgress, signal } = {}) {
  const uploadId = clientUploadId || newClientUploadId();

  const uploaded = await uploadImageBytes(projectId, file, {
    locationId,
    clientUploadId: uploadId,
    onProgress,
    signal,
  });

  return finalizeProjectImage({
    projectId,
    assetId: uploaded.asset_id,
    locationId,
    clientUploadId: uploadId,
  });
}

export async function rotateProjectImage({ projectId, imageId }) {
  const token = await getIdTokenForRequest();

  const mediaResponse = await fetch(
    `${MEDIA_BASE_URL}/vision/projects/${projectId}/images/${imageId}/rotate`,
    {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    },
  );

  if (!mediaResponse.ok) {
    if (mediaResponse.status === 403 || mediaResponse.status === 404) {
      throw new Error('You do not have access to rotate this image.');
    }
    throw new Error('Rotate failed. Please try again.');
  }

  const response = await client().rotateProjectImage({ params: { projectId, imageId } });

  if (!response.result?.image) {
    throw new Error('RotateProjectImage returned no image');
  }

  return projectImageToLegacy(response.result.image);
}

/** Persist an image's live mask regions (replace-all), returning the updated image record. */
export async function setImageMaskRegions({ projectId, imageId, regions }) {
  const response = await client().setImageMaskRegions({
    params: { projectId, imageId, regions: regionsToProto(regions) },
  });

  if (!response.result?.image) {
    throw new Error('SetImageMaskRegions returned no image');
  }

  return projectImageToLegacy(response.result.image);
}

/** List a project's images (paging through to load them all), as legacy image records. */
export async function listProjectImages(projectId, { locationId, shouldContinue } = {}) {
  const images = [];
  let pageToken = null;

  do {
    if (shouldContinue && !shouldContinue()) throw new DOMException('Image renewal paused', 'AbortError');
    const pagination = { pageSize: PAGE_SIZE };
    if (pageToken) {
      pagination.pageToken = { case: 'nextToken', value: pageToken };
    }

    const response = await client().listProjectImages({
      params: { projectId, locationId: locationId || '' },
      pagination,
    });

    const { images: pageImages, nextPageToken } = listResponseToImages(response);
    images.push(...pageImages);
    pageToken = nextPageToken;
  } while (pageToken);

  return images;
}

/** Move one image to a location (empty `locationId` = uncategorized); it lands at the front. */
export async function moveProjectImage({ projectId, imageId, locationId }) {
  await client().moveProjectImage({
    params: { projectId, imageId, locationId: locationId || '' },
  });
}

/** Move many images to a location (empty `locationId` = uncategorized); order is preserved. */
export async function bulkMoveProjectImages({ projectId, imageIds, locationId }) {
  await client().bulkMoveProjectImages({
    params: { projectId, imageIds, locationId: locationId || '' },
  });
}

/** Persist the order of `imageIds` within a location (empty `locationId` = uncategorized);
 * any listed image is also pinned to that location, so this covers move-with-position. */
export async function reorderProjectImages({ projectId, locationId, imageIds }) {
  await client().reorderProjectImages({
    params: { projectId, locationId: locationId || '', imageIds },
  });
}

/** Delete one image with its generations, reclaiming storage. */
export async function deleteProjectImage({ projectId, imageId }) {
  await client().deleteProjectImage({ params: { projectId, imageId } });
}

/** Delete every uncategorized image (no location) with its generations, reclaiming storage. */
export async function deleteUncategorizedImages({ projectId }) {
  await client().deleteUncategorizedImages({ params: { projectId } });
}

/**
 * Request AI auto-categorization for a set of uploaded images. The server queues the work
 * and returns immediately; the resulting location assignments arrive over realtime
 * (`image-categorized` / `images-bulk-categorized`), so this resolves to the queued count.
 */
export async function categorizeProjectImages({ projectId, assetIds }) {
  const response = await client().categorizeProjectImages({
    params: { projectId, assetIds },
  });

  return response.result?.queuedCount ?? 0;
}

async function postMediaUpload(url, file, clientUploadId, { signal } = {}) {
  const token = await getIdTokenForRequest();
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('client_upload_id', clientUploadId);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.timeout = MEDIA_UPLOAD_TIMEOUT_MS;

    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch {
          reject(new Error('Failed to parse the media upload response.'));
        }
      } else if (xhr.status === 413) {
        const err = new Error('Storage limit exceeded');
        err.code = STORAGE_LIMIT_EXCEEDED;
        reject(err);
      } else if (xhr.status === 403 || xhr.status === 404) {
        reject(new Error('You do not have access to upload here.'));
      } else {
        reject(new Error('Upload failed. Please try again.'));
      }
    };

    xhr.onerror = () => reject(new Error('Network error. Please check your connection.'));
    xhr.ontimeout = () => reject(new Error('Upload timed out. Please try again.'));

    if (signal) {
      signal.addEventListener('abort', () => {
        xhr.abort();
        reject(new Error('Upload cancelled'));
      });
    }

    xhr.send(formData);
  });
}

export async function uploadReferenceImage(projectId, file) {
  const clientUploadId = newClientUploadId();

  const uploaded = await postMediaUpload(
    `${MEDIA_BASE_URL}/vision/projects/${projectId}/reference-images`,
    file,
    clientUploadId,
  );

  const response = await client().finalizeReferenceImage({
    clientUploadId,
    params: { projectId, assetId: uploaded.asset_id },
  });

  if (!response.result?.reference) {
    throw new Error('FinalizeReferenceImage returned no reference');
  }

  return referenceImageToLegacy(response.result.reference);
}

export async function listReferenceBank(projectId, { shouldContinue } = {}) {
  if (shouldContinue && !shouldContinue()) throw new DOMException('Image renewal paused', 'AbortError');
  const response = await client().listReferenceBank({ params: { projectId } });

  return (response.result?.references ?? []).map(referenceImageToLegacy);
}

export async function deleteReferenceImage(projectId, assetId) {
  await client().deleteReferenceImage({ params: { projectId, assetId } });
}

export async function uploadSetDressingReference(projectId, file) {
  const clientUploadId = newClientUploadId();

  const uploaded = await postMediaUpload(
    `${MEDIA_BASE_URL}/vision/projects/${projectId}/set-dressing-references`,
    file,
    clientUploadId,
  );

  const response = await client().finalizeSetDressingReference({
    clientUploadId,
    params: { projectId, assetId: uploaded.asset_id },
  });

  if (!response.result?.reference) {
    throw new Error('FinalizeSetDressingReference returned no reference');
  }

  return referenceImageToLegacy(response.result.reference);
}

export async function listSetDressingBank(projectId, { shouldContinue } = {}) {
  if (shouldContinue && !shouldContinue()) throw new DOMException('Image renewal paused', 'AbortError');
  const response = await client().listSetDressingBank({ params: { projectId } });

  return (response.result?.references ?? []).map(referenceImageToLegacy);
}

export async function deleteSetDressingReference(projectId, assetId) {
  await client().deleteSetDressingReference({ params: { projectId, assetId } });
}

function derivePresetName(fileName) {
  const raw = (fileName || '')
    .replace(/\.[^/.]+$/, '')
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return raw.length > 0 ? raw.charAt(0).toUpperCase() + raw.slice(1) : 'Custom Ref';
}

// Both the unsubscribed-cap rejection and the storage-exhausted rejection arrive as
// `ResourceExhausted`; the `ErrorInfo` reason separates them so the picker shows the right toast
// (a reasonless one falls back to storage). Any other error passes through.
function toLegacyColorPresetError(error) {
  if (!(error instanceof ConnectError) || error.code !== Code.ResourceExhausted) {
    return error;
  }

  if (error.findDetails(ErrorInfoSchema)[0]?.reason === PRESET_LIMIT_REACHED) {
    const limit = new Error('Preset limit reached');
    limit.code = PRESET_LIMIT_REACHED;
    return limit;
  }

  const storage = new Error('Storage limit exceeded');
  storage.code = STORAGE_LIMIT_EXCEEDED;
  return storage;
}

export async function uploadColorPreset(file) {
  const clientUploadId = newClientUploadId();
  const name = derivePresetName(file?.name);

  const uploaded = await postMediaUpload(
    `${MEDIA_BASE_URL}/vision/color-presets`,
    file,
    clientUploadId,
  );

  let response;
  try {
    response = await client().finalizeColorPreset({
      clientUploadId,
      params: { assetId: uploaded.asset_id, name },
    });
  } catch (error) {
    throw toLegacyColorPresetError(error);
  }

  if (!response.result?.preset) {
    throw new Error('FinalizeColorPreset returned no preset');
  }

  return colorPresetToLegacy(response.result.preset);
}

export async function listColorPresets({ shouldContinue } = {}) {
  if (shouldContinue && !shouldContinue()) throw new DOMException('Image renewal paused', 'AbortError');
  const response = await client().listColorPresets({ params: {} });

  return (response.result?.presets ?? []).map(colorPresetToLegacy);
}

export async function deleteColorPreset(presetId) {
  await client().deleteColorPreset({ params: { presetId } });
}
